/**
 * Stateless SharePoint Replication Handler
 * JSON-only, checkpoint-based, zero local state
 */

import { createMappingFunctions } from './mappers.js';
import { getListEndpointFragment } from '../sharepoint/utils.js';
const { interval, EMPTY, Subject, of } = rxjs;
const { map } = rxjs.operators;
const { replicateRxCollection } = RxDB;

export class SharePointReplicationHandler {
  constructor(options) {
    this._validateOptions(options);

    this.spClient = options.spClient;
    this.collection = options.collection;
    this.listIdentifier = options.listIdentifier;
    this.listEndpoint = getListEndpointFragment(options.listIdentifier);
    this.primaryPath = options.collection.schema.primaryPath;
    this.fieldMapping = options.fieldMapping || {};
    this.pullBatchSize = options.pullBatchSize || 5000;
    this.pullInterval =
      options.pullInterval !== undefined ? options.pullInterval : 30000;

    this.spPrimaryField =
      this.fieldMapping[this.primaryPath] || this.primaryPath;
    this.spDeletedField = this.fieldMapping._deleted || '_deleted';

    this.spList = this.spClient.list(options.listIdentifier);

    this._initPromise = this._initialize(options);
  }

  _validateOptions(options) {
    if (!options.spClient || !options.listIdentifier || !options.collection) {
      throw new Error(
        'Missing required options: spClient, listIdentifier, collection'
      );
    }
  }

  async _initialize(options) {
    const listItemEntityTypeFullName =
      options.listItemEntityTypeFullName ||
      (await this._fetchListItemEntityTypeFullName());

    this.mappers = createMappingFunctions({
      primaryPath: this.primaryPath,
      fieldMapping: this.fieldMapping,
      rxdbSchema: this.collection.schema.jsonSchema,
      listItemEntityTypeFullName,
    });

    // Precompute query params once
    this.queryParams = {};
    if (this.mappers.selectFields.length > 0) {
      this.queryParams.$select = this.mappers.selectFields.join(',');
    }
    if (this.mappers.expandEntities.length > 0) {
      this.queryParams.$expand = this.mappers.expandEntities.join(',');
    }
  }

  async _fetchListItemEntityTypeFullName() {
    const res = await this.spClient.request(
      `web/lists/${this.listEndpoint}`,
      'GET'
    );
    return res.d.ListItemEntityTypeFullName;
  }

  async ensureInitialized() {
    await this._initPromise;
  }

  _buildEndpoint(baseEndpoint) {
    const params = [];
    if (this.queryParams.$select)
      params.push(`$select=${this.queryParams.$select}`);
    if (this.queryParams.$expand)
      params.push(`$expand=${this.queryParams.$expand}`);

    if (params.length === 0) return baseEndpoint;

    const separator = baseEndpoint.includes('?') ? '&' : '?';
    return `${baseEndpoint}${separator}${params.join('&')}`;
  }

  async pull({ lastCheckpoint, batchSize }) {
    await this.ensureInitialized();

    const cp = lastCheckpoint || { modified: '1900-01-01T00:00:00Z', id: 0 };
    const isInitialSync = cp.modified === '1900-01-01T00:00:00Z' && cp.id === 0;

    // Initial sync: use paginated fetch
    if (isInitialSync) {
      return await this._pullInitial();
    }

    // Incremental sync: checkpoint-based
    return await this._pullIncremental(cp, batchSize);
  }

  async _pullInitial() {
    try {
      const items = await this.spList.fetchPaginatedItems({
        batchSize: this.pullBatchSize,
        maxItemsCount: 50000,
        ...this.queryParams,
      });

      const sorted = items.sort((a, b) => {
        if (a.Modified !== b.Modified)
          return a.Modified.localeCompare(b.Modified);
        return a.Id - b.Id;
      });

      return {
        documents: sorted
          .map(this.mappers.mapToRxDoc)
          .filter(
            item =>
              typeof item[this.primaryPath] !== 'string' ||
              item[this.primaryPath] === item[this.primaryPath].trim()
          ),
        checkpoint: sorted.length
          ? {
              modified: sorted[sorted.length - 1].Modified,
              id: sorted[sorted.length - 1].Id,
            }
          : { modified: '1900-01-01T00:00:00Z', id: 0 },
      };
    } catch (error) {
      if (error.name === 'ListTooLargeError') {
        throw new Error(
          `List too large: ${error.totalCount} items exceeds maximum ${error.maxItemsCount}`
        );
      }
      throw error;
    }
  }

  async _pullIncremental(cp, batchSize) {
    const size = batchSize || this.pullBatchSize;
    const filter = `(Modified gt datetime'${cp.modified}') or (Modified eq datetime'${cp.modified}' and Id gt ${cp.id})`;

    try {
      const baseEndpoint = `web/lists/${this.listEndpoint}/items?$filter=${encodeURIComponent(filter)}&$orderby=Modified asc,Id asc&$top=${size}`;
      const endpoint = this._buildEndpoint(baseEndpoint);
      const res = await this.spClient.request(endpoint, 'GET');
      const items = res.d.results;

      return {
        documents: items
          .map(this.mappers.mapToRxDoc)
          .filter(
            item =>
              typeof item[this.primaryPath] !== 'string' ||
              item[this.primaryPath] === item[this.primaryPath].trim()
          ),
        checkpoint: items.length
          ? {
              modified: items[items.length - 1].Modified,
              id: items[items.length - 1].Id,
            }
          : cp,
      };
    } catch (error) {
      // View threshold exceeded - fallback to paginated fetch
      if (error.isListViewThreshold) {
        return await this._pullInitial();
      }
      throw error;
    }
  }

  async push(rows) {
    await this.ensureInitialized();
    const results = await Promise.all(rows.map(row => this._pushRow(row)));
    return results.filter(Boolean);
  }

  async _pushRow(row) {
    try {
      const { newDocumentState: doc, assumedMasterState: assumed } = row;

      // Query current raw SP item
      const spItem = await this._querySpItemByPrimaryKey(doc[this.primaryPath]);

      // Check conflict using collection's conflict handler
      const current = spItem ? this.mappers.mapToRxDoc(spItem) : null;
      if (current) {
        // Missing assumedMasterState means the local meta has no record of
        // this document on master — treat the existing server doc as a
        // conflict to avoid reverting it with stale local state.
        if (!assumed) {
          return current;
        }
        const isConflict = !this.collection.conflictHandler.isEqual(
          current,
          assumed
        );
        if (isConflict) {
          return current;
        }
      }

      // Execute operation (errors handled in each method)
      if (spItem) {
        return await this._update(doc, spItem);
      } else {
        return await this._insert(doc);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async _insert(doc) {
    const spItem = this.mappers.mapToSpItem(doc);
    const res = await this.spClient.request(
      `web/lists/${this.listEndpoint}/items`,
      'POST',
      { body: JSON.stringify(spItem), rawResponse: true },
      true
    );

    if (res.ok) {
      const data = await res.json();
      return this.mappers.mapToRxDoc(data.d);
    }

    // 500: Unique constraint violation - fetch existing
    if (res.status === 500) {
      const text = await res.text();
      if (this._isUniqueConstraintError(text)) {
        return await this._fetchCurrentOrMarkDeleted(doc);
      }
    }

    // Non-retryable client errors: deny insert
    if (res.status >= 400 && res.status < 500) {
      return this._markAsDeleted(doc);
    }

    // Retryable errors (network, server errors)
    throw { status: res.status, message: res.statusText };
  }

  async _update(doc, spItem) {
    const res = await this.spClient.request(
      `web/lists/${this.listEndpoint}/items(${spItem.Id})`,
      'PATCH',
      {
        body: JSON.stringify(this.mappers.mapToSpItem(doc)),
        headers: {
          'If-Match': spItem.__metadata.etag,
          'X-HTTP-Method': 'MERGE',
        },
        rawResponse: true,
      },
      true
    );

    if (res.ok) {
      return null;
    }

    // 404: Item deleted server-side
    if (res.status === 404) {
      return doc._deleted ? null : this._markAsDeleted(doc);
    }

    if (res.status === 500) {
      const text = await res.text();
      if (this._isUniqueConstraintError(text)) {
        return await this._fetchCurrentOrMarkDeleted(doc);
      }
    }

    if (res.status >= 400 && res.status < 500) {
      return await this._fetchCurrentOrMarkDeleted(doc);
    }

    // Retryable errors
    throw { status: res.status, message: res.statusText };
  }

  async _querySpItemByPrimaryKey(value) {
    const filter = `${this.spPrimaryField} eq '${value}'`;
    const baseEndpoint = `web/lists/${this.listEndpoint}/items?$filter=${encodeURIComponent(filter)}`;
    const endpoint = this._buildEndpoint(baseEndpoint);
    const res = await this.spClient.request(endpoint, 'GET');

    const results = res.d.results;
    if (results.length === 0) return null;
    if (results.length > 1) {
      throw new Error(
        `Primary key violation: ${value} returned ${results.length} items`
      );
    }

    return results[0]; // Return raw SP item
  }

  _isUniqueConstraintError(text) {
    const lower = text.toLowerCase();
    return lower.includes('already in use') || lower.includes('duplicate');
  }

  async _fetchCurrentOrMarkDeleted(doc) {
    const spItem = await this._querySpItemByPrimaryKey(doc[this.primaryPath]);
    return spItem ? this.mappers.mapToRxDoc(spItem) : this._markAsDeleted(doc);
  }

  async _fetchCurrent(doc) {
    const spItem = await this._querySpItemByPrimaryKey(doc[this.primaryPath]);
    return spItem ? this.mappers.mapToRxDoc(spItem) : null;
  }

  _markAsDeleted(doc) {
    return { ...doc, _deleted: true };
  }
}

export async function replicateWithSharePoint(options) {
  const handler = new SharePointReplicationHandler(options);
  await handler.ensureInitialized();

  const pull =
    options.pull !== false
      ? {
          handler: (checkpoint, batchSize) =>
            handler.pull({ lastCheckpoint: checkpoint, batchSize }),
          batchSize: options.pullBatchSize || 5000,
        }
      : undefined;

  if (pull && handler.pullInterval) {
    pull.stream$ = interval(handler.pullInterval).pipe(map(() => 'RESYNC'));
  }

  return replicateRxCollection({
    collection: options.collection,
    replicationIdentifier: `sharepoint-${options.listIdentifier}-${options.collection.name}`,
    live: options.live ?? true,
    pull,
    push:
      options.push !== false
        ? {
            handler: rows => handler.push(rows),
            batchSize: options.pushBatchSize || 1,
          }
        : undefined,
  });
}
